import { Skeleton } from "@mui/material";
import { ErrorMessage, FormikProps } from "formik";
import { useEffect, useState } from "react";
import { BiSearch } from "react-icons/bi";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMCheckbox from "src/components/UI/atoms/formFields/ATMCheckbox/ATMCheckbox";
import ATMFilePickerWrapper from "src/components/UI/atoms/formFields/ATMFileUploader/ATMFileUploaderWrapper";
import ATMHTMLEditor from "src/components/UI/atoms/formFields/ATMHTMLEditor/ATMHTMLEditor";
import ATMInputAdormant from "src/components/UI/atoms/formFields/ATMInputAdormant/ATMInputAdormant";
import ATMMultiSelect from "src/components/UI/atoms/formFields/ATMMultiSelect/ATMMultiSelect";
import ATMTagAutoComplete from "src/components/UI/atoms/formFields/ATMTagAutoComplete/ATMTagAutoComplete";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import useQuestionTechnology from "src/hooks/useQuestionTechnology";
import { TestSeriesFormValues } from "src/models/TestSeries.model";
import { useGetAllQuestionTagQuery } from "src/services/QuestionCategoryServices";
import { useGetAllTestQuestionWithPaginationQuery } from "src/services/TestQuestionServices";
import { handleValidNumber } from "src/utils/validations/numberInput";

type Props = {
  formikProps: FormikProps<TestSeriesFormValues>;
  editorValue?: string;
  formType: "Add" | "Edit";
};

const TestSeriesForm = ({ formikProps, editorValue, formType }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  const [testQuestionData, setTestQuestionData] = useState<any>();
  const [selectAllQuestion, setSelectAllQuestion] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { questionTechnology, isDataLoading: isQuestionTechnologyLoading } =
    useQuestionTechnology();
  const { data: AllTagsData } = useGetAllQuestionTagQuery("");
  const { data, isLoading, isFetching } =
    useGetAllTestQuestionWithPaginationQuery(
      {
        limit: 100,
        searchValue: searchValue,
        params: ["questionTitle"],
        page: 1,
        filterBy: [],
        questionCategory: [],
        questionTechnology:
          values?.questionTechnologyId?.map(
            (technology: any) => technology?.value
          ) || [],
        questionTag: values?.questionTag || [],
        dateFilter: {},
        orderBy: "createdAt",
        orderByValue: 1,
        isPaginationRequired: false,
      },
      {
        skip:
          !values?.questionTechnologyId?.length || !values?.questionTag?.length,
      }
    );

  const tagsOptions = AllTagsData?.data?.map((el: any) => ({
    title: el?.tagName,
  }));

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setTestQuestionData(data?.data);
    }
  }, [data, isLoading, isFetching]);

  const handleQuestionSelect = (question: any) => {
    const selectedQuestions = values?.questionOptions || [];
    setFieldValue(
      "questionOptions",
      selectedQuestions.find((q: any) => q._id === question._id)
        ? selectedQuestions.filter((q: any) => q._id !== question._id)
        : [...selectedQuestions, question]
    );
  };

  const handleSelectAll = () => {
    if (selectAllQuestion) {
      setFieldValue("questionOptions", []);
    } else {
      setFieldValue("questionOptions", testQuestionData);
    }
    setSelectAllQuestion(!selectAllQuestion);
  };

  const filterQuestionsByTechnology = (
    selectedTechnologies: any[],
    questions: any[]
  ) => {
    const selectedTechnologyIds = selectedTechnologies.map(
      (tech) => tech.value
    );
    const newQuestions = questions.filter((question) =>
      question.questionTechnologyId.some((techId: string) =>
        selectedTechnologyIds.includes(techId)
      )
    );
    setFieldValue("questionOptions", newQuestions);
  };

  const filterQuestionsByTags = (selectedTags: any[], questions: any[]) => {
    const newQuestions = questions.filter((question) =>
      question.questionTag.some((tag: string) => selectedTags.includes(tag))
    );
    setFieldValue("questionOptions", newQuestions);
  };

  return (
    <div className="flex flex-col flex-1 h-full gap-4 p-4 overflow-hidden">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-medium text-slate-700">
          {formType === "Add" ? "Add Test Series" : "Update Test Series"}
        </h1>
      </div>
      <div className="flex flex-col w-full h-full gap-4 overflow-scroll">
        <div className="grid items-center w-full grid-cols-4 gap-4">
          {/* Title */}
          <div className="col-span-2">
            <ATMTextField
              required
              name="questionTitle"
              value={values.questionTitle}
              onChange={(e) => setFieldValue("questionTitle", e.target.value)}
              label="Title"
              placeholder="Enter Title"
              onBlur={handleBlur}
            />
          </div>
          <ATMTextField
            showInfo={true}
            tooltipInfoTitle="MRP price that can be charged for this test"
            name="oldPrice"
            value={values.oldPrice}
            onChange={(e) =>
              handleValidNumber(e) && setFieldValue("oldPrice", e.target.value)
            }
            label="MRP"
            placeholder="Enter MRP"
            onBlur={handleBlur}
          />
          <ATMTextField
            showInfo={true}
            tooltipInfoTitle="The discounted price at which this test is being sold"
            required
            name="price"
            value={values.price}
            onChange={(e) =>
              handleValidNumber(e) && setFieldValue("price", e.target.value)
            }
            label="Offer Price"
            placeholder="Enter Offer Price"
            onBlur={handleBlur}
          />
          <ATMTextField
            required
            name="passingPercent"
            value={values.passingPercent}
            onChange={(e) =>
              handleValidNumber(e) && setFieldValue("passingPercent", e.target.value)
            }
            label="Passing Percent"
            placeholder="Enter Passing Percent"
            onBlur={handleBlur}
          />
          <ATMTextField
            required
            name="testTime"
            value={values.testTime}
            onChange={(e) =>
              handleValidNumber(e) && setFieldValue("testTime", e.target.value)
            }
            label="Test Time(mintues)"
            placeholder="Enter Test Time"
            onBlur={handleBlur}
          />
          <ATMFilePickerWrapper
            required
            name="testImage"
            label="Test Series Image"
            placeholder="Select file"
            selectedFile={values.testImage}
            onSelect={(imgUrl) => setFieldValue("testImage", imgUrl)}
          />
        </div>

        {/* description */}
        <div className="z-0">
          <ATMHTMLEditor
            required
            name="testDescription"
            key={editorValue}
            label="Description"
            value={values?.testDescription}
            placeholder="Write here...."
            onChange={(newValue: any) => {
              setFieldValue("testDescription", newValue);
            }}
          />
        </div>

        {/* Technology */}
        <ATMMultiSelect
          required
          name="questionTechnologyId"
          value={values.questionTechnologyId}
          onBlur={handleBlur}
          label="Technology"
          placeholder="Select Technology"
          options={questionTechnology?.map((el: any) => ({
            label: el?.questionTechnology,
            value: el?._id,
          }))}
          isLoading={isQuestionTechnologyLoading}
          onChange={(newValue) => {
            setFieldValue("questionTechnologyId", newValue);
            setFieldValue("questionTag", []);
            filterQuestionsByTechnology(newValue, values?.questionOptions);
          }}
        />
        {/* Tags */}
        <ATMTagAutoComplete
          placeholder="Enter tag"
          name="questionTag"
          value={values.questionTag}
          onChange={(value: any) => {
            setFieldValue("questionTag", value);
            filterQuestionsByTags(value, values?.questionOptions);
          }}
          label="Tags"
          required
          option={tagsOptions || []}
        />
        {values?.questionTechnologyId?.length || values?.questionTag?.length ? (
          <div className="relative h-full overflow-hidden mt-1">
            <div className="relative grid h-full grid-cols-2 gap-4 overflow-hidden">
              {/* Questions */}
              <div className="h-full overflow-hidden border rounded">
                {isLoading || isFetching ? (
                  <div className="p-2 ">
                    <Skeleton height={50} animation="wave" className="w-full" />
                    <Skeleton height={50} animation="wave" className="w-full" />
                    <Skeleton height={50} animation="wave" className="w-full" />
                    <Skeleton height={50} animation="wave" className="w-full" />
                  </div>
                ) : (
                  <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-2 border-b">
                      <div className="flex items-center gap-4">
                        <div className="font-medium text-gray-700">
                          Questions
                        </div>
                        <div className="flex items-center gap-2">
                          <ATMCheckbox
                            inputClasses="h-3 w-3"
                            checked={selectAllQuestion}
                            onChange={handleSelectAll}
                            extraClasses="px-2 py-1 rounded"
                            label={`Select All ${values?.questionOptions?.length}`}
                          />
                        </div>
                      </div>
                      <div className="w-[250px]">
                        <ATMInputAdormant
                          name=""
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          adormant={<BiSearch />}
                          adormantProps={{
                            position: "start",
                            extraClasses: "bg-white border-0",
                          }}
                          inputProps={{ className: "bg-white" }}
                          placeholder="Search..."
                          size="small"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col h-full overflow-auto">
                      {testQuestionData?.map((question: any) => (
                        <div key={question._id} className="items-center w-full">
                          <ATMCheckbox
                            checked={values.questionOptions.some(
                              (q: any) => q._id === question._id
                            )}
                            onChange={() => handleQuestionSelect(question)}
                            extraClasses="p-2"
                            label={question?.questionTitle}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Selected Questions */}
              <div className="flex flex-col h-full overflow-hidden border rounded">
                <div className="p-2 font-medium text-gray-700 border-b">
                  Selected Questions: {values?.questionOptions?.length}
                </div>

                <div className="flex flex-col h-full overflow-auto">
                  {values.questionOptions?.length === 0 ? (
                    <div className="flex items-center justify-center w-full h-full">
                      No Question Selected
                    </div>
                  ) : (
                    values.questionOptions.map((question: any) => (
                      <div
                        key={question._id}
                        className="flex items-center justify-between p-2 hover:bg-gray-100 group/item"
                      >
                        <span className="text-gray-800">
                          {question?.questionTitle}
                        </span>
                        <ATMLoadingButton
                          className="text-sm w-fit h-[30px] bg-white font-medium text-neutral border border-transparent group-hover/item:text-red-700 group-hover/item:border group-hover/item:border-red-800 group-hover/item:bg-red-100"
                          onClick={() => handleQuestionSelect(question)}
                        >
                          Remove
                        </ATMLoadingButton>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <ErrorMessage name="questionOptions">
              {(errMsg) => (
                <p className="font-poppins absolute text-[14px] text-start mt-0 text-red-500">
                  {errMsg}
                </p>
              )}
            </ErrorMessage>
          </div>
        ) : null}
      </div>
      <div className="flex items-center justify-end w-full pt-4 border-t h-fit">
        <ATMLoadingButton
          isLoading={isSubmitting}
          type="submit"
          className="w-32"
        >
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default TestSeriesForm;
